// Drives the opponent in single player, and keeps the game going once you
// have beaten it.
//
// The opponent acts through the same dispatch a player uses, on a short delay
// so its turn is something you watch rather than something that has already
// happened by the time the board redraws.
//
// Running the opponent out of moves is the stage boundary — mate or stalemate,
// since a side that cannot move has been beaten either way. It used to be
// *eradication* — strip the opponent to its king and a random piece appeared —
// which meant the game could never actually be won: the last enemy piece was
// replaced before you could mate the king behind it, so the mate that ends a
// chess game never arrived. Nothing is conjured now until you earn it.
//
// **A class over the store, not a hook** (PLAN-CLIENT-SPLIT day 7). The hook
// this was stalled four times, each time an effect that set `busy` and never
// reached `done()`, or a dependency list that missed something. `wake` runs on
// every store change and every `configure`, and `busy` is cleared by the
// wedge timer whatever path forgot to.

import type { Branch, DriverOptions, Side as ApiSide } from "src/api";
import type { Side } from "src/engine/rules/board";
import { isGameOver } from "src/engine/rules/chess/status";
import type { FloorCommand } from "src/engine/rules/turns";
import { spawnHoldsNext } from "src/engine/rules/campaign/pending";
import { seizedBy } from "src/engine/rules/campaign/scepter";
import { despairStep } from "src/engine/ai/depression";
import { atTurnStart, despairSlot } from "src/engine/rules/campaign/despair";
import { profileById, profileForSide } from "src/engine/ai/profiles";
import type { Task } from "src/engine/ai/tasks";
import { OpponentPlanner, type PlannerOptions } from "src/engine/ai/planner";
import { chooseWave, stagesCleared, stageTurnsOver, type Wave } from "src/engine/ai/waves";
import {
  type GrowthPlan,
  stageCommand,
  type StageOffers,
  stageOffers
} from "src/engine/ai/runStep";
import { turnDrain } from "src/engine/ai/turnPace";
import { withModeLight } from "src/engine/ai/modeLight";
import { branchOf } from "src/engine/session/branchOffers";
import { track } from "src/engine/telemetry/analytics";

import { Driver, type DriverDoor } from "./driver";

/**
 * A `playerSide` no side can ever be, for a game with nobody sitting at it.
 *
 * The driver plays for *whichever army is on the clock and is not the
 * player's*, so a side that never matches means it plays for everybody —
 * watch mode with no second code path.
 */
export const NOBODY: Side = -1;

/** Between the opponent's individual commands, so its turn reads as a turn. */
const STEP_MS = 260;

/** How often an idle worker is asked whether it is still answering. */
const HEARTBEAT_MS = 5000;

/** Long enough for the mate to land on screen before the next front opens. */
const STAGE_MS = 1400;

/**
 * How long the opponent may go without doing anything before it is unwedged.
 *
 * Wide on purpose: a plan has 1.5s and then a step per command, and a stage
 * boundary takes `STAGE_MS` plus a camera move on top. Anything past this is
 * not slow, it is stuck.
 */
const WEDGED_MS = 6000;

/**
 * After the first, one line per this many restarts.
 *
 * A worker that misses its deadline every turn is already the problem; a line
 * each turn on top of it is a flood that costs more than the misses did. The
 * shape `server/refusals.ts` uses — say the first immediately, count the rest.
 * **A count and never a clock**: what is worth knowing is how many.
 */
const RESTART_REPORT_EVERY = 10;

let restarts = 0;

/**
 * A planner that gave up, said out loud.
 *
 * `giveUp` terminates the worker, spawns a fresh one and plays `naiveTurn` —
 * no reply search, a move and nothing else — and after three in a row drops
 * to `looseTurn`, a random legal move. **A fallback is faster than a plan**,
 * so the failure makes the board livelier rather than slower, which is the
 * one shape nobody reads as a fault. Hence the line.
 */
function reportRestart(reason: "timeout" | "error"): void {
  restarts++;
  if (restarts === 1 || restarts % RESTART_REPORT_EVERY === 0) {
    console.warn(
      `opponent: planner ${reason} — worker replaced, playing a fallback move ` +
        `(${restarts} so far). The board plays faster and worse while this happens.`
    );
  }
}

/**
 * How long the camera gets to travel before the thing it is looking at
 * happens. Single player is the only mode where the board can be much bigger
 * than the viewport *and* the other player will not wait, so the camera
 * leads: it goes to where the thing is about to happen, then the thing happens.
 */
const LOOK_MS = 420;

/**
 * How long the camera stays on new ground before handing the view back.
 * Longer than a move's look, because a whole front arriving is worth reading;
 * bounded, because leaving it there opens the player's next turn with their
 * own factory off screen.
 */
const STAGE_LOOK_MS = 1600;


/** The knobs, with their defaults: `configure({})` plays nothing. */
export const DEFAULT_OPTIONS: DriverOptions = {
  enabled: false,
  playerSide: null,
  level: "easy",
  pace: 1,
  paused: false,
  step: 0,
  branching: false,
  lead: 1,
  fast: false,
  waveDelay: 0
};

export interface OpponentDriverOptions {
  /** Injected so a test needs no worker. */
  planner?: Partial<PlannerOptions>;
}

export class OpponentDriver extends Driver {
  options: DriverOptions = DEFAULT_OPTIONS;

  /**
   * True while a plan is still draining into the store, so a wake in the
   * middle of one does not start a second. Cleared by `done`, and by the wedge
   * timer when nothing reached `done`.
   */
  private busy = false;

  /**
   * What the opponent is in the middle of doing (`ai/tasks.ts`), its depth
   * banks and its belt ages, per side. **Held here rather than in the
   * worker**: the supervisor terminates and respawns a worker that misses its
   * deadline, so memory living in one dies with it — and how long a lane
   * survives would become a fact about how fast the client is.
   */
  private tasks: Task[] = [];
  private banks: number[] = [];
  private beltAges: Map<number, number>[] = [];

  /** The opponent thinks in a worker, supervised. */
  private readonly planner: OpponentPlanner;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private wedge: ReturnType<typeof setTimeout> | null = null;

  /** The last step this driver has already spent. */
  private stepped = 0;
  /** The turn slot despair was last judged on (`despairSlot`), its game, and the sides judged. */
  private checked = -1;
  private checkedGame: unknown = undefined;
  private readonly seen = new Set<number>();

  /**
   * The front waiting on the player, or null. **Derived, never stored in the
   * game**: the offers are a pure function of the seed and the stage (§19.3),
   * so a reload rebuilds the same pair, and nothing about a pending question
   * belongs in a log that replays.
   */
  private pending: StageOffers | null = null;
  private pendingView: Branch | null = null;

  constructor(door: DriverDoor, options: OpponentDriverOptions = {}) {
    super(door);
    this.planner = new OpponentPlanner({
      spawn: () =>
        new Worker(new URL("../ai/opponent.worker.ts", import.meta.url), { type: "module" }),
      onRestart: reportRestart,
      ...options.planner
    });
  }

  configure(options: Partial<DriverOptions>): void {
    const was = this.options;
    this.options = { ...was, ...options };
    if (this.options.enabled !== was.enabled) this.pulse(this.options.enabled);
    this.wake();
  }

  branch(): Branch | null {
    return this.pendingView;
  }

  /**
   * Ask an idle worker whether it is still there. The plan deadline catches a
   * worker that hangs *while thinking*; this catches one that died between
   * turns, so it is replaced during the player's turn rather than discovered
   * when it is next asked to move.
   */
  private pulse(on: boolean): void {
    if (this.heartbeat !== null) clearInterval(this.heartbeat);
    this.heartbeat = on ? setInterval(() => this.planner.ping(), HEARTBEAT_MS) : null;
  }

  private done(): void {
    this.busy = false;
    this.wake();
  }

  /** The seat the driver will not play, in the rules' number. */
  private get playerSide(): Side {
    return this.options.playerSide ?? NOBODY;
  }

  protected step(): void {
    const { enabled, paused, level } = this.options;
    const { state } = this.door.store.getSnapshot();
    const turn = state.position.turn;
    const over = isGameOver(state.status);

    // A turn the player has seized (§31 A4) is the player's to play.
    this.armWedge(enabled && !over && !paused && turn !== this.playerSide && seizedBy(state) === undefined);

    // **Depression, once a turn.** An army that could not win even if its
    // opponent stopped moving is not in a position, it is in a coma (see
    // `ai/depression.ts`). Every living side, enemies included (§26 F9): the
    // whole decision is `despairStep`'s; what this keeps is the timing — once
    // per turn — and the dispatching. The verdict crosses
    // as an ordinary command, so it is in the log and it replays.
    //
    // **Once a turn means at its start** (§31 T): keyed on the turn slot, not
    // `turnCounter` — ticks, which a move advances mid-turn — and skipped for
    // a slot first seen mid-turn. A new game is a new key.
    if (enabled && !over && !paused) {
      const { log } = this.door.store.getSnapshot();
      if (log[0] !== this.checkedGame) {
        this.checkedGame = log[0];
        this.checked = -1;
      }
      const slot = despairSlot(state.position);
      if (this.checked !== slot) {
        this.checked = slot;
        if (atTurnStart(state.position)) {
          for (const command of despairStep(state, level, this.seen)) {
            this.door.dispatch(command);
          }
        }
      }
    }

    this.playTurn();
  }

  /**
   * Nothing has happened for far too long: unwedge it.
   *
   * `busy` is the one flag that can leave the driver returning early for the
   * rest of the game, and it has been arrived at from four directions. So
   * rather than chase the fifth: if it is the opponent's turn, the game is
   * not over, and nothing has moved for `WEDGED_MS`, clear it and try again.
   * Not while paused — nothing is *meant* to be happening, so "nothing has
   * happened" is the state rather than the symptom.
   */
  private armWedge(on: boolean): void {
    if (this.wedge !== null) clearTimeout(this.wedge);
    this.wedge = on
      ? setTimeout(() => {
          this.wedge = null;
          if (this.disposed || !this.busy) return;
          this.busy = false;
          this.wake();
        }, WEDGED_MS)
      : null;
  }

  private playTurn(): void {
    const { enabled, paused, step, level, branching, waveDelay, profiles, tuning } = this.options;
    if (!enabled || this.busy) return;
    // Paused, and no step outstanding. The turn in flight is not affected:
    // this only decides whether a *new* one starts.
    if (paused && step === this.stepped) return;
    this.stepped = step;

    const snap = this.door.store.getSnapshot();
    const { state, log } = snap;
    const turn = state.position.turn;
    const playerSide = this.playerSide;

    // It has no move: that army collapses into scrap and the next one
    // arrives. Only when the side with no move is *its* — your own mate or
    // stalemate is a loss, and a loss is allowed to be a loss. `stageTurnsOver`
    // rather than the test written out here, because the run director has to
    // hold its region reveal until this has landed and the two answers must be
    // the same one.
    if (stageTurnsOver(state, playerSide)) {
      // **`FloorCommand`, like every other thing the director sends** (§29 U2):
      // dispatched on whoever's clock it is, so Undo must never see it as the
      // player's last action.
      const closeFront = (delay: number): FloorCommand => ({
        type: "clear-front",
        side: state.position.turn,
        delay
      });

      // **Under the spawn clock, one of several enemies falling closes its
      // front without landing** (§26 F10, `spawnHoldsNext`), or two live
      // factions would be permanent.
      const hold = spawnHoldsNext(state);
      if (hold !== null) {
        this.door.dispatch(closeFront(hold));
        return;
      }

      // **Close the front now, land the camp later.** Guarded on `pending`
      // being unset, or the turn the camp comes due this would close the front
      // a second time — and on the board actually *being* beaten, since the
      // boundary also fires on clocks where there is no front to close.
      if (
        waveDelay > 0 &&
        state.run &&
        !state.run.pending &&
        (state.status === "checkmate" || state.status === "stalemate")
      ) {
        this.door.dispatch(closeFront(waveDelay));
        return;
      }

      // Already asked, and waiting on an answer. Before generating, because
      // `stageOffers` builds several camps against a cloned board.
      if (this.pending) return;

      const branch = stageOffers(state, level);
      if (!branch) {
        // **Loudly.** A camp with nowhere to land leaves the board sitting in
        // checkmate forever, and every part of that failure is silent.
        console.error(
          `stage ${stagesCleared(state) + 1}: no camp could be placed, so the front cannot turn over`
        );
        return;
      }

      // **The player's turn to decide, and the run stops for it.** Nothing is
      // landed and no camera is moved: the panel is about to take the screen.
      if (branching) {
        this.setPending(branch);
        return;
      }

      // **A stage with no camp to land is one where ground comes back.**
      this.land(
        branch,
        branch.offers.length > 0
          ? chooseWave(state, branch.offers, state.run?.side ?? 0)
          : branch.grow!
      );
      return;
    }

    // Play for whichever army is on the clock and is not the player's.
    // **Not a seized turn** (§31 A4): the Oil-Stone Scepter's holder plays it.
    if (isGameOver(state.status) || turn === playerSide || seizedBy(state) !== undefined) return;

    this.busy = true;

    // **The seat's own choice, then the game's.**
    const profile = profileById(profiles?.[turn] ?? profileForSide(state, turn));
    const { pace, lead, fast, clockHealth } = this.options;
    void this.planner
      .plan(
        // **The mode light, on the planner's copy only** (§32 L1): factory
        // until a Monarch is threatened. The store, log and hash never see it.
        withModeLight(state, turn),
        turn,
        level,
        // The profile's own knobs, unless the caller has set them by hand.
        { ...profile.tuning, ...tuning },
        this.tasks,
        profile.weights,
        this.banks[turn] ?? 0,
        this.beltAges[turn] ?? new Map()
      )
      .then(({ commands, tasks: carried, bank, beltAges: aged, stance, value, fallback }) => {
        if (this.disposed) return;
        // **Before anything is dispatched**, so `log.length` is still the index
        // this turn's commands are about to start at. Told even about a turn
        // that spends nothing.
        this.door.emit.plan({
          side: turn as ApiSide,
          at: log.length,
          commands: commands.length,
          stance,
          value,
          fallback
        });
        // Only when the planner actually answered: a fallback reports no task
        // list rather than an empty one, and overwriting the plan with nothing
        // on the turn a worker died would throw it away when it matters.
        if (carried) this.tasks = carried;
        if (bank !== undefined) this.banks[turn] = bank;
        if (aged !== undefined) this.beltAges[turn] = new Map(aged);
        if (commands.length === 0) {
          this.done();
          return;
        }

        // Where this turn is about to happen, so the camera can get there
        // first. The one look that happens every single turn.
        const move = commands.find((command) => command.type === "move");
        if (move) this.look([move.from, move.to], "move");

        // **The camera pans *while* the free half of the turn happens**, and
        // the one thing that waits for it is the move. **One schedule, decided
        // up front** (`turnPace.ts`), with the board's own clock folded in
        // once for the whole turn.
        const health = clockHealth?.() ?? 1;
        const drain = turnDrain(commands.length, move ? commands.indexOf(move) : -1, {
          stepMs: STEP_MS / pace / health,
          panMs: LOOK_MS / (pace * lead * health),
          fast
        });
        let index = 0;
        let beat = 0;
        const next = (): void => {
          if (this.disposed) return;
          if (beat >= drain.beats.length) {
            // Only after the last command, and only when we borrowed the view
            // in the first place — a turn with no move never took it.
            if (move) {
              if (drain.after > 0) this.later(() => this.returnView(), drain.after);
              else this.returnView();
            }
            this.done();
            return;
          }
          const { count } = drain.beats[beat];
          beat++;
          // **Dispatched in one go**, which is what makes a fast turn parallel
          // rather than merely quick: the board sees one diff.
          // **A plan is only good for the turn it was made on.** It drains
          // over beats, and it ends in `pass` (§31 T2) — which is legal for
          // whoever holds the clock — so a stale tail landing after the
          // board moved on would end somebody else's turn. Off our turn, or
          // once a command is refused, the rest is dropped.
          for (let i = 0; i < count && index < commands.length; i++, index++) {
            const live = this.door.store.getSnapshot().state;
            if (live.position.turn !== turn || !this.door.dispatch(commands[index])) {
              index = commands.length;
              beat = drain.beats.length;
              break;
            }
          }
          if (beat < drain.beats.length) this.later(next, drain.beats[beat].wait);
          else next();
        };
        if (drain.beats.length > 0) {
          if (drain.beats[0].wait > 0) this.later(next, drain.beats[0].wait);
          else next();
        } else {
          this.done();
        }
      });
  }

  private setPending(front: StageOffers | null): void {
    this.pending = front;
    const { state } = this.door.store.getSnapshot();
    this.pendingView = front ? branchOf(front, state.tally) : null;
    this.door.emit.branch(this.pendingView);
  }

  /**
   * Land a camp, with the camera choreography a front deserves. **One body
   * for both paths**: the automatic chooser and the player's pick differ only
   * in *which* wave arrives.
   */
  private land(branch: StageOffers, choice: Wave | GrowthPlan): void {
    this.busy = true;
    // Look toward the new front first. The ground does not exist yet, so the
    // rig clamps this to the current edge — the "something is happening over
    // there" gesture. A regrowth's seed is where the ground comes back.
    const cells = "cells" in choice ? choice.cells : [choice.seed];
    this.look(cells, "region");
    const { pace } = this.options;
    this.later(() => {
      const { state } = this.door.store.getSnapshot();
      // **Checked**, because `dispatch` answers `false` for a command the
      // reducer refused and swallows it — and this one lands the whole front.
      const landed = this.door.dispatch(stageCommand(state, branch, choice));
      if (!landed) {
        console.error(
          `stage ${branch.stage}: the board stopped being a mate before the camp landed`
        );
      }
      // Now the ground is real, so the same request settles on the camp
      // itself — and then the view goes back where the player left it.
      this.look(cells, "region");
      this.later(() => this.returnView(), STAGE_LOOK_MS / pace);
      this.done();
    }, STAGE_MS / pace);
  }

  /**
   * **Clearing `pending` first, and unconditionally.** Whatever happens to the
   * command, the question has been answered — leaving it set would put the
   * panel back up over a board that has already grown.
   */
  pick(index?: number): void {
    const pending = this.pending;
    if (!pending) return;
    const { state } = this.door.store.getSnapshot();
    // **The regrowth sits one past the camps**, so the panel can offer it as
    // another card without the index meaning two different things.
    const extra = index !== undefined && index === pending.offers.length;
    const chosen: Wave | GrowthPlan =
      (extra ? pending.grow : undefined) ??
      (index === undefined ? undefined : pending.offers[index]?.wave) ??
      // No index, or one that names nothing: take what the machine would have
      // taken (§19.6 hazard 3). A board with no camp falls through to the
      // regrowth, the only thing left to land.
      (pending.offers.length > 0
        ? chooseWave(state, pending.offers, state.run?.side ?? 0)
        : pending.grow!);
    // **Which way the board was grown, counted here** — the only place the
    // answer exists. The `next-stage` that lands carries the camp, not which
    // of the offers it was, so a log cannot say whether anybody takes the
    // harder fight. `pick()` with no argument is the machine's choice standing
    // in for a player who dismissed it, and that is worth telling apart.
    const offer = index === undefined ? undefined : pending.offers[index];
    track("stage_branch_chosen", {
      stage: pending.stage,
      offers: pending.offers.length,
      choice: index ?? -1,
      by: index === undefined ? "auto" : "player",
      camp: offer?.wave.formation ?? "unknown",
      facing: offer?.facing ?? -1
    });
    this.setPending(null);
    this.land(pending, chosen);
  }

  override dispose(): void {
    super.dispose();
    this.pulse(false);
    this.armWedge(false);
    this.planner.dispose();
  }
}
